(function () {
  'use strict';

  // Quick Buy: fetches a server-rendered condensed PDP (sections/quick-buy-content.liquid)
  // via the Section Rendering API and re-uses assets/pdp.js's initPdp() controller against
  // it — the exact same variant/qty/gallery/lightbox logic the real PDP uses, so the two
  // surfaces can never drift out of sync. This file only owns: the drawer chrome (open /
  // close / loading state), the cart/drawer trigger routing, and the two purchase actions
  // that only exist inside Quick Buy (Add to Bag via AJAX, Buy Now).

  var drawer, inner;

  function init() {
    drawer = document.querySelector('[data-quick-shop]');
    if (!drawer) return;
    inner = drawer.querySelector('[data-qs-inner]');

    document.addEventListener('click', onDocumentClickCapture, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer && !drawer.hidden) closeDrawer();
    });
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-qs-close]')) closeDrawer();
    });
  }

  function onDocumentClickCapture(e) {
    var trigger = e.target.closest && e.target.closest('[data-quick-add]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();

    var target = trigger.getAttribute('data-quick-add-target'); // 'cart' | 'drawer'
    var singleId = trigger.getAttribute('data-quick-add-single-variant-id');
    var handle = trigger.getAttribute('data-quick-add-handle');

    // Mobile corner "+" — quiet add-to-cart, never opens Quick Buy, never redirects. Uses
    // the card's default/first-available variant (data-variant-id, always present on every
    // card regardless of variant count) for both single- and multi-variant products, so the
    // "+" always adds to cart immediately instead of sending multi-variant products off to
    // the PDP — shoppers can still open the full PDP or Quick Buy if they want to pick a
    // different variant first.
    if (target === 'cart') {
      var cartVariantId = trigger.getAttribute('data-variant-id') || singleId;
      if (cartVariantId) {
        addToCartQuietly(trigger, cartVariantId);
      } else if (handle) {
        window.location.href = '/products/' + handle;
      }
      return;
    }

    // Quick Buy — ALWAYS opens the mini-PDP drawer. No fast path to checkout here, ever,
    // regardless of how many variants the product has. This also fires correctly when the
    // trigger is a "Pairs Well With" card rendered *inside* an already-open drawer, since
    // this listener is attached to `document` in the capture phase: it simply reopens /
    // re-fetches into the same drawer, replacing its content instead of stacking a second one.
    if (!handle) return;
    openQuickBuy(handle);
  }

  function openQuickBuy(handle) {
    openDrawer();
    showLoading();
    fetch('/products/' + handle + '?section_id=quick-buy-content', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('section fetch failed');
        return r.text();
      })
      .then(function (html) {
        inner.innerHTML = html;
        inner.scrollTop = 0;
        var panel = drawer.querySelector('.qs-drawer__panel');
        if (panel) panel.scrollTop = 0;

        var root = inner.querySelector('.pdp-page');
        if (!root) return;

        if (window.HounddalePDP && typeof window.HounddalePDP.initPdp === 'function') {
          window.HounddalePDP.initPdp(root);
        }
        wireQuickBuyActions(root);
        syncStickyBar(root);
        root.addEventListener('pdp:variantchange', function () { syncStickyBar(root); });
      })
      .catch(function () {
        inner.innerHTML = '<div class="qs-error">Sorry, we couldn’t load this product. Please try again.</div>';
      });
  }

  function wireQuickBuyActions(root) {
    var addBtn = root.querySelector('[data-qb-add]');
    var buyBtn = root.querySelector('[data-qb-buy]');
    if (addBtn) {
      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        submitQuickBuy(root, addBtn, false);
      });
    }
    if (buyBtn) {
      buyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        submitQuickBuy(root, buyBtn, true);
      });
    }
  }

  // Add to Bag (goToCheckout=false) and Buy Now (goToCheckout=true) both read the CURRENT
  // selected variant id + quantity straight out of the DOM at click time — never a variant
  // captured earlier — so the correct variant/quantity is always what gets purchased even if
  // the shopper changed options right before clicking.
  function submitQuickBuy(root, btn, goToCheckout) {
    if (!root || !btn || btn.disabled) return;
    var vin = root.querySelector('[data-pdp-variant-input]');
    var qtyInput = root.querySelector('[data-pdp-qty]');
    var variantId = vin && vin.value;
    var qty = parseInt(qtyInput && qtyInput.value, 10) || 1;
    if (!variantId) return;

    var originalHTML = btn.innerHTML;
    btn.disabled = true;
    if (!goToCheckout) btn.innerHTML = '<span>Adding…</span>';

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw new Error((err && err.description) || 'add failed'); });
      return r.json();
    }).then(function () {
      if (goToCheckout) {
        window.location.href = '/checkout';
        return;
      }
      return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (cart) {
        document.querySelectorAll('.cart-count').forEach(function (el) { el.textContent = cart.item_count; });
        btn.innerHTML = '<span>Added ✓</span>';
        setTimeout(function () {
          btn.disabled = false;
          btn.innerHTML = originalHTML;
        }, 1400);
      });
    }).catch(function () {
      btn.disabled = false;
      btn.innerHTML = originalHTML;
    });
  }

  // Mobile sticky purchase bar lives in the static drawer shell (outside the scrollable
  // area) so it stays pinned while the shopper scrolls the injected content. It mirrors
  // whatever the main in-content price/Buy button currently show — resynced on every
  // pdp:variantchange event dispatched by pdp.js's updateUI().
  function syncStickyBar(root) {
    if (!drawer || !root) return;
    var stickyBar = drawer.querySelector('[data-qs-sticky]');
    if (!stickyBar) return;
    var priceEl = root.querySelector('[data-pdp-price]');
    var mainBuy = root.querySelector('[data-qb-buy]');
    var stickyPriceEl = drawer.querySelector('[data-qs-sticky-price]');
    var stickyBuyBtn = drawer.querySelector('[data-qs-sticky-buy]');

    stickyBar.setAttribute('data-visible', '');
    if (stickyPriceEl && priceEl) stickyPriceEl.textContent = priceEl.textContent;
    if (stickyBuyBtn) {
      stickyBuyBtn.disabled = !mainBuy || mainBuy.disabled;
      stickyBuyBtn.onclick = function () {
        var currentBuy = root.querySelector('[data-qb-buy]');
        if (currentBuy && !currentBuy.disabled) submitQuickBuy(root, currentBuy, true);
      };
    }
  }

  function openDrawer() {
    if (!drawer) return;
    drawer.hidden = false;
    document.body.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(function () { drawer.classList.add('is-open'); });
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    var stickyBar = drawer.querySelector('[data-qs-sticky]');
    if (stickyBar) stickyBar.removeAttribute('data-visible');
    setTimeout(function () {
      drawer.hidden = true;
      document.body.style.overflow = '';
      if (inner) inner.innerHTML = '';
    }, 260);
  }

  function showLoading() {
    if (inner) inner.innerHTML = '<div class="qs-loading">Loading…</div>';
    var stickyBar = drawer.querySelector('[data-qs-sticky]');
    if (stickyBar) stickyBar.removeAttribute('data-visible');
  }

  // Mobile corner "+": quiet add-to-cart, no drawer, no navigation.
  function addToCartQuietly(btn, variantId) {
    if (!btn || btn.disabled) return;
    var CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
    var originalHTML = btn.innerHTML;
    btn.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    }).then(function () {
      btn.classList.add('is-added');
      btn.innerHTML = CHECK;
      return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
    }).then(function (cart) {
      document.querySelectorAll('.cart-count').forEach(function (el) { el.textContent = cart.item_count; });
      setTimeout(function () {
        btn.classList.remove('is-added');
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      }, 1400);
    }).catch(function () {
      btn.disabled = false;
      btn.innerHTML = originalHTML;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
